import React, { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { PlusIcon } from "lucide-react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Separator,
} from "@f2e/ui/components/base"
import { cn } from "@f2e/ui/lib/utils"

import { CopilotHiddenField } from "@/components/Copilot/CopilotHiddenField"
import { Agent, AgentType } from "@/types"

// Type definitions
export interface AgentBreadcrumbSelectorProps {
  agents: Agent[]
  currentAgent?: Agent
  onAgentSelect: (agent: Agent, hasUnsavedChanges: boolean) => void
  onCreateNewAgent?: (agentType: AgentType, hasUnsavedChanges: boolean) => void
  hasUnsavedChanges?: boolean
  isCreating?: boolean
  className?: string
}

interface AgentSelectItemProps {
  agent: Agent
}

// Constants - moved to top level for better organization
const CREATE_BASIC_VALUE = "__create_basic__" as const

type CreateActionValue = typeof CREATE_BASIC_VALUE

// Type guard for create actions
const isCreateAction = (value: string): value is CreateActionValue => {
  return value === CREATE_BASIC_VALUE
}

// Sub-components for better organization
const AgentSelectItem = React.memo(({ agent }: AgentSelectItemProps) => (
  <div className="flex items-center gap-1">
    <span className="truncate" title={agent.name}>
      {agent.name}
    </span>
  </div>
))

AgentSelectItem.displayName = "AgentSelectItem"

const CreateAgentItem = React.memo(() => {
  const { t } = useTranslation()

  // Derive translation key from agent type
  const translationKey = "workspace.agent.breadcrumb.createNewAgent"

  return (
    <div className="flex items-center gap-2 relative">
      <PlusIcon size={16} className="text-muted-foreground absolute -left-6" />
      <span>{t(translationKey)}</span>
    </div>
  )
})

CreateAgentItem.displayName = "CreateAgentItem"

// Main component
const AgentBreadcrumbSelector = ({
  agents,
  currentAgent,
  onAgentSelect,
  onCreateNewAgent,
  hasUnsavedChanges = false,
  isCreating = false,
  className,
}: AgentBreadcrumbSelectorProps) => {
  const { t } = useTranslation()

  // Memoized handlers for better performance
  const handleAgentChange = useCallback(
    (value: string) => {
      // Handle create new agent actions
      if (isCreateAction(value)) {
        const agentType = AgentType.Basic
        onCreateNewAgent?.(agentType, hasUnsavedChanges)
        return
      }

      // Handle existing agent selection
      const selectedAgent = agents.find(
        (agent) => agent.id.toString() === value
      )
      if (selectedAgent) {
        onAgentSelect(selectedAgent, hasUnsavedChanges)
      } else {
        console.warn(`Agent with id ${value} not found in agents list`)
      }
    },
    [agents, onAgentSelect, onCreateNewAgent, hasUnsavedChanges]
  )

  // Memoized computed values
  const currentDisplayName = useMemo(() => {
    const fallbackName = isCreating
      ? t("workspace.agentPlayground.configPanel.create")
      : t("workspace.agentPlayground.configPanel.untitled")
    return currentAgent?.name || fallbackName
  }, [currentAgent?.name, isCreating, t])

  const currentAgentValue = useMemo(() => {
    return currentAgent?.id.toString() || ""
  }, [currentAgent?.id])

  // Early return for no agents case
  if (agents.length === 0) {
    return (
      <span
        role="link"
        aria-disabled="true"
        aria-current="page"
        aria-label={`Current agent: ${currentDisplayName}`}
        className={cn(
          "font-normal text-lg text-slate-950 dark:text-slate-50 inline-flex items-center gap-1",
          className
        )}
      >
        {currentDisplayName}
      </span>
    )
  }

  return (
    <Select
      value={currentAgentValue}
      onValueChange={handleAgentChange}
      aria-label="Select agent or create new agent"
    >
      <SelectTrigger
        className={cn(
          "h-auto p-0 border-none shadow-none !bg-transparent hover:!bg-transparent font-normal text-lg text-slate-950 dark:text-slate-50 hover:text-slate-700 dark:hover:text-slate-300 transition-colors inline-flex items-center gap-1 focus:ring-0 focus:ring-offset-0",
          className
        )}
        aria-label={`Current agent: ${currentDisplayName}. Click to select different agent or create new one.`}
      >
        <SelectValue placeholder={currentDisplayName} />
      </SelectTrigger>
      <SelectContent position="popper" align="start" className="min-w-[250px]">
        {/* Existing agents */}
        {agents.map((agent) => (
          <SelectItem
            key={agent.id}
            value={agent.id.toString()}
            aria-label={`Select agent: ${agent.name} (${agent.type})`}
          >
            <AgentSelectItem agent={agent} />
          </SelectItem>
        ))}

        <Separator />

        {/* Create new agent options */}
        <SelectItem
          value={CREATE_BASIC_VALUE}
          aria-label="Create new Basic agent"
        >
          <CreateAgentItem />
        </SelectItem>
      </SelectContent>
      {/* Snapshot helpers (hidden): selected agent, options list, and create availability */}
      <CopilotHiddenField
        fieldKey="breadcrumb.agentSelector.selected"
        value={currentDisplayName}
      />
      <CopilotHiddenField
        fieldKey="breadcrumb.agentSelector.options"
        value={agents.map((a) => a.name).join(" | ")}
      />
    </Select>
  )
}

AgentBreadcrumbSelector.displayName = "AgentBreadcrumbSelector"

export default AgentBreadcrumbSelector
